import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

import useOutsideClick from "../../../hooks/useOutsideClick";

import { IoClose } from "react-icons/io5";

/**
 * ModalWrapper component to display a modal dialog (portaled to `document.body`).
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.isOpen - Indicates if the modal is open.
 * @param {Function} props.onClose - Function to close the modal.
 * @param {React.ReactNode} props.children - The content of the modal.
 * @param {boolean} props.closeButton - Whether to show the close button (default: true).
 * @param {boolean} props.closeOnOutsideClick - Whether clicking outside closes the modal (default: true).
 * @param {boolean} props.retainDataOnClose - When true, keep modal content mounted when closed so form state is preserved; hide with CSS (default: false).
 * @param {string} props.className - Optional custom className for the modal container.
 * @param {string} [props.overlayClassName='z-40'] - Tailwind / utility classes merged onto the full-screen overlay (e.g. z-index above other portaled UI).
 * @param {Object} [props.contentProps] - Spread onto the modal surface element (the node with `ref`, e.g. `data-*` for host-app outside-click rules).
 * @param {string} [props.outsideClickIgnoreSelector] - If set, mousedown targets inside a matching ancestor do not count as outside the surface for `useOutsideClick`.
 *
 * @returns {JSX.Element|null} The rendered ModalWrapper component or null if not open (unless retainDataOnClose).
 */
const ModalWrapper = ({
  isOpen,
  onClose,
  children,
  closeButton = true,
  closeOnOutsideClick = true,
  retainDataOnClose = false,
  maxWidth = 680,
  className = "",
  overlayClassName = "z-40",
  contentProps,
  outsideClickIgnoreSelector,
}) => {
  const modalRef = useRef(null);
  useOutsideClick(
    modalRef,
    closeOnOutsideClick && isOpen ? () => onClose() : () => {},
    outsideClickIgnoreSelector
      ? { ignoreSelector: outsideClickIgnoreSelector }
      : undefined
  );

  // Prevent body scroll only when the modal is actually open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // When closed and not retaining, unmount entirely
  if (!isOpen && !retainDataOnClose) return null;

  const defaultClasses = `bg-white rounded-lg shadow-lg relative w-full max-h-[90vh] overflow-y-auto p-2`;
  const containerClasses = className || defaultClasses;
  const overlayHidden = retainDataOnClose && !isOpen;

  return createPortal(
    <div
      className={`fixed font-inter inset-0 flex items-center justify-center bg-black bg-opacity-50 ${overlayClassName} ${overlayHidden ? "invisible pointer-events-none" : ""}`}
      aria-hidden={overlayHidden}
    >
      <div
        className={containerClasses}
        ref={modalRef}
        style={{ maxWidth: maxWidth }}
        {...(contentProps || {})}
      >
        {closeButton && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 hover:text-gray-800 transition-colors ease-in-out duration-200"
          >
            <IoClose className="text-xl text-gray-600 m-auto" />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

ModalWrapper.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  retainDataOnClose: PropTypes.bool,
  overlayClassName: PropTypes.string,
  contentProps: PropTypes.object,
  outsideClickIgnoreSelector: PropTypes.string,
};

export default ModalWrapper;
